import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Upload, LayoutTemplate } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';

interface Partner { id: number; name: string; }

interface ReportTemplate {
  id: number;
  partner_id: number;
  name: string;
  logo_url: string | null;
  brand_color: string;
  company_name: string | null;
  intro_text: string | null;
  sections: string[];
  language: 'en' | 'it';
  version: number;
  // Frontespizio (2026-10-20): dati del system integrator e testi di copertina.
  company_info: CompanyInfo;
}

interface CompanyInfo {
  address?: string; vat_number?: string; email?: string; phone?: string; website?: string;
  cover_title?: string; cover_note?: string;
}

const COMPANY_FIELDS: { key: keyof CompanyInfo; label: string; placeholder?: string }[] = [
  { key: 'address', label: 'Address', placeholder: 'Street, ZIP, City, Country' },
  { key: 'vat_number', label: 'VAT number' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
];

const ALL_SECTIONS: { key: string; label: string }[] = [
  { key: 'cover_page', label: 'Cover page' },
  { key: 'management_summary', label: 'Management summary (page 2)' },
  { key: 'executive_summary', label: 'Executive Summary' },
  { key: 'incidents_by_severity', label: 'Incidents by Severity' },
  { key: 'top_threats', label: 'Top Threats' },
  { key: 'sla_metrics', label: 'SLA Metrics' },
  { key: 'compliance_status', label: 'Operational Compliance Indicators' },
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'historical_trends', label: 'Historical Trend' },
  { key: 'night_assets', label: 'Assets Active at Night' },
  { key: 'exposure', label: 'Exposure (vulnerabilities, unsupported software)' },
  { key: 'coverage', label: 'Protection and monitoring coverage' },
  { key: 'notable_incidents', label: 'Notable incidents and communications' },
  { key: 'remote_access', label: 'Remote access tools' },
  { key: 'threat_origin', label: 'Origin of the threats' },
  { key: 'inventory', label: 'Inventory and changes' },
  { key: 'ai_insights', label: 'AI security considerations' },
];

// Editor dei template di report brandizzati per partner (logo, colore, sezioni abilitate) — vedi
// docs/FEATURES.md §7. Un platform admin sceglie il partner da un menu, un partner admin/analyst
// vede/gestisce solo i propri.
export default function ReportTemplatesPage() {
  const { user, isAdmin } = useAuth();
  // I template sono del partner: un utente di livello tenant li vede (li usa per generare i
  // report) ma non li crea, modifica o elimina — il backend lo rifiuta comunque.
  const canManage = isAdmin && (user?.scopeLevel !== 'tenant');
  // Con un tenant scelto nel menu "Viewing" si mostrano i template del SUO system integrator
  // (2026-10-20, richiesta dell'utente: anche da platform admin), e il menu dei partner è
  // sostituito dal nome del partner. Con "All tenants" resta la scelta del partner.
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(user?.partnerId ?? null);
  const [tenantPartnerId, setTenantPartnerId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<Partial<ReportTemplate>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.scopeLevel === 'platform') {
      axios.get('/api/partners').then(res => {
        setPartners(res.data.data);
        if (!selectedPartnerId && res.data.data[0]) setSelectedPartnerId(res.data.data[0].id);
      });
    }
  }, [user]);

  useEffect(() => {
    if (!selectedTenantId) { setTenantPartnerId(null); return; }
    axios.get(`/api/tenants/${selectedTenantId}`)
      .then(res => setTenantPartnerId(res.data.data.partner_id))
      .catch(() => setTenantPartnerId(null));
  }, [selectedTenantId]);

  // Partner effettivo: quello del tenant scelto, altrimenti quello del menu (o dell'utente).
  const effectivePartnerId = selectedTenantId ? tenantPartnerId : selectedPartnerId;
  const effectivePartnerName = partners.find(p => p.id === effectivePartnerId)?.name ?? null;

  async function load() {
    if (!effectivePartnerId) { setTemplates([]); return; }
    const res = await axios.get('/api/report-templates', { params: { partnerId: effectivePartnerId } });
    setTemplates(res.data.data);
  }

  useEffect(() => { load(); }, [effectivePartnerId]);

  function startNew() {
    setEditingId('new');
    setForm({ name: '', brand_color: '#4f46e5', company_name: '', intro_text: '', sections: ALL_SECTIONS.map(s => s.key), language: 'en', company_info: {} });
  }

  function startEdit(t: ReportTemplate) {
    setEditingId(t.id);
    setForm({ ...t, company_info: t.company_info || {} });
  }

  function updateCompany(key: keyof CompanyInfo, value: string) {
    setForm(prev => ({ ...prev, company_info: { ...(prev.company_info || {}), [key]: value } }));
  }

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, logo_url: reader.result as string }));
    reader.readAsDataURL(file);
  }

  function toggleSection(key: string) {
    setForm(prev => {
      const current = prev.sections || [];
      return { ...prev, sections: current.includes(key) ? current.filter(s => s !== key) : [...current, key] };
    });
  }

  async function save() {
    if (!effectivePartnerId || !form.name?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        partnerId: effectivePartnerId,
        name: form.name,
        logoUrl: form.logo_url,
        brandColor: form.brand_color,
        companyName: form.company_name,
        introText: form.intro_text,
        sections: form.sections,
        language: form.language,
        companyInfo: form.company_info || {},
      };
      if (editingId === 'new') {
        await axios.post('/api/report-templates', payload);
      } else {
        await axios.put(`/api/report-templates/${editingId}`, payload);
      }
      setEditingId(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the template');
    } finally {
      setSaving(false);
    }
  }

  async function remove(t: ReportTemplate) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await axios.delete(`/api/report-templates/${t.id}`);
    load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
          <LayoutTemplate size={20} /> Report Templates
        </h1>
        <div className="flex items-center gap-2">
          {user?.scopeLevel === 'platform' && selectedTenantId && (
            <span className="text-sm text-gray-500" title="System integrator of the tenant selected in the Viewing menu">
              {effectivePartnerName ? <><span className="font-medium text-gray-700">{effectivePartnerName}</span> · system integrator of {selectedTenantName}</> : 'Loading…'}
            </span>
          )}
          {user?.scopeLevel === 'platform' && !selectedTenantId && (
            <select value={selectedPartnerId ?? ''} onChange={e => setSelectedPartnerId(Number(e.target.value))} className="input-field">
              {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {canManage && (
            <button onClick={startNew} className="btn-primary flex items-center gap-1"><Plus size={16} /> New template</button>
          )}
        </div>
      </div>

      {editingId !== null && (
        <div className="card p-4 mb-4">
          {error && <div className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Template name</label>
              <input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="input-field w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Company name (shown on report header)</label>
              <input value={form.company_name || ''} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} className="input-field w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Primary color</label>
              <input type="color" value={form.brand_color || '#4f46e5'} onChange={e => setForm(p => ({ ...p, brand_color: e.target.value }))} className="input-field w-20 h-9" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Language</label>
              <select value={form.language || 'en'} onChange={e => setForm(p => ({ ...p, language: e.target.value as 'en' | 'it' }))} className="input-field w-full">
                <option value="en">English</option>
                <option value="it">Italiano</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Logo</label>
              <div className="flex items-center gap-3">
                {form.logo_url && <img src={form.logo_url} alt="logo preview" className="h-10 border border-gray-200 rounded" />}
                <label className="btn-secondary flex items-center gap-1 cursor-pointer">
                  <Upload size={14} /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Intro text</label>
              <textarea value={form.intro_text || ''} onChange={e => setForm(p => ({ ...p, intro_text: e.target.value }))}
                rows={3} className="input-field w-full" placeholder="Optional introduction shown at the top of the report…" />
            </div>
            <div className="md:col-span-2 border-t border-gray-100 pt-4">
              <div className="text-xs font-medium text-gray-700 mb-1">Cover page</div>
              <p className="text-xs text-gray-400 mb-3">
                Shown on the first page when the "Cover page" section is enabled: your company details below as
                "Prepared by", the customer's details (name, address, VAT, contact — edited per tenant in the Tenants
                page) as "Prepared for".
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cover title</label>
                  <input value={form.company_info?.cover_title || ''} onChange={e => updateCompany('cover_title', e.target.value)}
                    placeholder="Security Report" className="input-field w-full" />
                </div>
                {COMPANY_FIELDS.map(f => (
                  <div key={f.key}>
                    <label className="block text-xs text-gray-500 mb-1">{f.label}</label>
                    <input value={form.company_info?.[f.key] || ''} onChange={e => updateCompany(f.key, e.target.value)}
                      placeholder={f.placeholder} className="input-field w-full" />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Cover note (confidentiality, distribution…)</label>
                  <textarea value={form.company_info?.cover_note || ''} onChange={e => updateCompany('cover_note', e.target.value)}
                    rows={2} className="input-field w-full" placeholder="Confidential — for the addressee only." />
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-2">Sections</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ALL_SECTIONS.map(s => (
                  <label key={s.key} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={(form.sections || []).includes(s.key)} onChange={() => toggleSection(s.key)} />
                    {s.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                "AI security considerations" adds a closing page written by an AI model from the period's incidents,
                vulnerabilities, log source availability and asset monitoring. It needs the Anthropic API key in
                Platform Settings; without it the page says the section is not available.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
            <button onClick={() => setEditingId(null)} className="btn-secondary">Cancel</button>
            <button onClick={save} disabled={saving || !form.name?.trim()} className="btn-primary disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Name</th><th className="table-header">Company</th>
            <th className="table-header">Sections</th><th className="table-header">Version</th><th className="table-header"></th>
          </tr></thead>
          <tbody>
            {templates.map(t => (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="table-cell font-medium">{t.name}</td>
                <td className="table-cell text-gray-500">{t.company_name || '—'}</td>
                <td className="table-cell text-gray-500">{t.sections.length} sections</td>
                <td className="table-cell text-gray-500">v{t.version}</td>
                <td className="table-cell">
                  {canManage && (
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(t)} className="p-1 text-gray-400 hover:text-primary-600" title="Edit">
                        <LayoutTemplate size={14} />
                      </button>
                      <button onClick={() => remove(t)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={5}>No template created yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
